import React, { Component, Fragment, useState, useRef, useEffect } from 'react';
import { Formik, Form, Field, FieldArray } from 'formik';
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import { Query, Mutation } from 'react-apollo';
import { useQuery, useMutation } from 'react-apollo-hooks';
import gql from 'graphql-tag';
import classNames from 'classnames';
import Select from 'react-select';
import CreatableSelect from 'react-select/lib/Creatable';
import Textarea from 'react-autosize-textarea';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import InlineEditor from '@ckeditor/ckeditor5-build-inline';
import DOMPurify from 'dompurify';

import useBodyScrollLock from '../../hooks/useBodyScrollLock';

import { QA_LIST_QUERY, felsen } from '../../routes/qa';

const TAGS_QUERY = gql`
  query {
    tags {
      id
      name
    }
  }
`;

const ADD_QA_MUTATION = gql`
  mutation addQa(
    $questions: [QaQuestionInput]
    $slangQuestions: [QaQuestionInput]
    $answers: [QaAnswerInput]
    $tags: [Int]
  ) {
    addQa(
      questions: $questions
      answers: $answers
      slangQuestions: $slangQuestions
      tags: $tags
    )
  }
`;

const ADD_TAG_MUTATION = gql`
  mutation addTag($name: String!) {
    addTag(name: $name) {
      id
      name
    }
  }
`;

const languages = [
  { value: 'finnish', label: 'Suomi' },
  { value: 'swedish', label: 'Ruotsi' },
  {
    value: 'english',
    label: 'Englanti'
  }
];

function isValidNewOption(inputValue, selectValue, selectOptions) {
  return !(
    !inputValue ||
    selectValue.some(option => compareOption(inputValue, option)) ||
    selectOptions.some(option => compareOption(inputValue, option))
  );
}

const compareOption = (inputValue, option) => {
  const candidate =
    typeof inputValue === 'string' ? inputValue.toLowerCase() : inputValue;
  if (typeof option.value === 'string') {
    if (option.value.toLowerCase() === candidate) {
      return true;
    }
  }
  if (typeof option.label === 'string') {
    if (option.label.toLowerCase() === candidate) {
      return true;
    }
  }
  return option.value === candidate || option.label === candidate;
};

const TagSelect = ({ options, value, onChange, onBlur }) => {
  // Add Tag mutation
  const [addTag, { error: addTagError, loading: addTagLoading }] = useMutation(
    ADD_TAG_MUTATION
  );

  const handleChange = (value, actionMeta) => {
    onChange('tags', value);
  };

  const handleBlur = event => {
    // this is going to call setFieldTouched and manually update touched.tags
    onBlur('tags', true);
  };

  const handleCreate = async inputValue => {
    const {
      data: { addTag: addedTag }
    } = await addTag({
      variables: {
        name: inputValue
      },
      refetchQueries: () => ['tags'],
      awaitRefetchQueries: true
    });

    onChange('tags', [...value, { value: addedTag.id, label: addedTag.name }]);
  };

  return (
    <CreatableSelect
      id="tag"
      options={options}
      isMulti
      isDisabled={addTagLoading}
      isLoading={addTagLoading}
      onChange={handleChange}
      onCreateOption={handleCreate}
      onBlur={handleBlur}
      value={value}
      isValidNewOption={isValidNewOption}
    />
  );
};

const AddQaModal = ({ initialValues = {}, closeModal }) => {
  const scrollable = useRef();
  useBodyScrollLock(scrollable);

  // Add QA mutation
  const [addQa, { error: addQaError, loading: addQaLoading }] = useMutation(
    ADD_QA_MUTATION
  );

  // Available tags
  const { loading, error, data } = useQuery(TAGS_QUERY);

  if (loading) {
    return (
      <Fragment>
        <div className="modal-header">
          <div className="modal-header-title">Lisää QA</div>
          <div className="modal-header-close" onClick={closeModal}>
            Sulje
          </div>
        </div>
        <div className="modal-content">Loading...</div>
      </Fragment>
    );
  }

  if (error) {
    return (
      <Fragment>
        <div className="modal-header">
          <div className="modal-header-title">Lisää QA</div>
          <div className="modal-header-close" onClick={closeModal}>
            Sulje
          </div>
        </div>
        <div className="modal-content">Error! {error.message}</div>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <div className="modal-header">
        <div className="modal-header-title">Lisää QA</div>
        <div className="modal-header-close" onClick={closeModal}>
          Sulje
        </div>
      </div>
      <Formik
        initialValues={{
          questions: [
            {
              text: initialValues.question || '',
              lang: 'finnish'
            }
          ],
          slangQuestions: [],
          answers: [
            {
              text: initialValues.text || '',
              lang: initialValues.lang || 'finnish'
            }
          ],
          tags: []
        }}
        onSubmit={async (payload, { setSubmitting }) => {
          const { questions, answers, slangQuestions, tags } = payload;

          setSubmitting(true);

          // Map tags to tagIds
          const tagIds = tags ? tags.map(tag => tag.value) : [];

          await addQa({
            variables: {
              questions,
              answers,
              slangQuestions,
              tags: tagIds
            },
            refetchQueries: () => ['qas'],
            awaitRefetchQueries: true
          });

          closeModal();
        }}
        render={({ values, handleSubmit, isSubmitting }) => (
          <Fragment>
            <div className="modal-content" ref={scrollable}>
              <div className="modal-content-form">
                <Form>
                  <div className="modal-content-form-header">Kysymykset</div>
                  <FieldArray
                    name="questions"
                    render={arrayHelpers => (
                      <div className="add-qa-questions">
                        {values.questions &&
                          values.questions.map((question, index) => (
                            <div key={index} className="add-qa-question">
                              <Field
                                name={`questions[${index}].lang`}
                                render={({
                                  field: { name, value, onChange },
                                  form
                                }) => {
                                  return (
                                    <div className="add-qa-question-lang">
                                      <Select
                                        onChange={val => {
                                          form.setFieldValue(name, val.value);
                                        }}
                                        options={languages}
                                        value={languages.filter(
                                          lang => lang.value === value
                                        )}
                                      />
                                    </div>
                                  );
                                }}
                              />
                              <div className="add-qa-question-text">
                                <Field
                                  name={`questions[${index}].text`}
                                  render={({
                                    field,
                                    form: { isSubmitting }
                                  }) => (
                                    <Textarea
                                      {...field}
                                      rows={1}
                                      disabled={isSubmitting}
                                    />
                                  )}
                                />
                              </div>
                              <div className="add-qa-question-remove">
                                {index > 0 && (
                                  <button
                                    type="button"
                                    className="add-qa-question-remove-button"
                                    onClick={() => arrayHelpers.remove(index)} // remove a friend from the list
                                  >
                                    x
                                  </button>
                                )}
                              </div>
                              {index === values.questions.length - 1 && (
                                <div className="add-qa-question-add">
                                  <button
                                    type="button"
                                    className="add-qa-question-add-button"
                                    onClick={() =>
                                      arrayHelpers.push({
                                        text: '',
                                        lang: 'finnish'
                                      })
                                    } // insert an empty string at a position
                                  >
                                    + Lisää
                                  </button>
                                </div>
                              )}
                            </div>
                          ))}
                      </div>
                    )}
                  />
                  <div className="modal-content-form-header">
                    Slangivariaatiot
                  </div>
                  <FieldArray
                    name="slangQuestions"
                    render={arrayHelpers => (
                      <div className="add-qa-questions">
                        {values.slangQuestions &&
                          values.slangQuestions.map((question, index) => (
                            <div key={index} className="add-qa-question">
                              <Field
                                name={`slangQuestions[${index}].lang`}
                                render={({
                                  field: { name, value, onChange },
                                  form
                                }) => {
                                  return (
                                    <div className="add-qa-question-lang">
                                      <Select
                                        onChange={val => {
                                          form.setFieldValue(name, val.value);
                                        }}
                                        options={languages}
                                        value={languages.filter(
                                          lang => lang.value === value
                                        )}
                                      />
                                    </div>
                                  );
                                }}
                              />
                              <Field
                                name={`slangQuestions[${index}].text`}
                                render={({ field, form: { isSubmitting } }) => (
                                  <Textarea
                                    {...field}
                                    rows={1}
                                    disabled={isSubmitting}
                                  />
                                )}
                              />
                              <div className="add-qa-question-remove">
                                <button
                                  type="button"
                                  className="add-qa-question-remove-button"
                                  onClick={() => arrayHelpers.remove(index)} // remove a friend from the list
                                >
                                  x
                                </button>
                              </div>
                            </div>
                          ))}
                        <div className="add-qa-question-add">
                          <button
                            type="button"
                            className="add-qa-question-add-button"
                            onClick={() =>
                              arrayHelpers.push({
                                text: '',
                                lang: 'finnish'
                              })
                            } // insert an empty string at a position
                          >
                            + Lisää
                          </button>
                        </div>
                      </div>
                    )}
                  />
                  <div className="modal-content-form-header">Vastaukset</div>
                  <FieldArray
                    name="answers"
                    render={arrayHelpers => (
                      <div className="add-qa-answers">
                        {values.answers &&
                          values.answers.map((friend, index) => (
                            <div key={index} className="add-qa-answer">
                              <Field
                                name={`answers[${index}].lang`}
                                render={({
                                  field: { name, value, onChange },
                                  form
                                }) => {
                                  return (
                                    <div className="add-qa-question-lang">
                                      <Select
                                        onChange={val => {
                                          form.setFieldValue(name, val.value);
                                        }}
                                        options={languages}
                                        value={languages.filter(
                                          lang => lang.value === value
                                        )}
                                      />
                                    </div>
                                  );
                                }}
                              />
                              <div className="add-qa-answer-text">
                                <Field
                                  name={`answers[${index}].text`}
                                  render={({
                                    field,
                                    form: { isSubmitting, setFieldValue }
                                  }) => (
                                    <CKEditor
                                      editor={InlineEditor}
                                      config={{
                                        toolbar: ['bold', 'link'],
                                        link: {
                                          decorators: {
                                            openInNewTab: {
                                              mode: 'manual',
                                              label: 'Open in a new tab',
                                              defaultValue: true,
                                              attributes: {
                                                target: '_blank',
                                                rel: 'noopener noreferrer'
                                              }
                                            }
                                          }
                                        },
                                        autoParagraph: false
                                      }}
                                      onReady={editor => {
                                        if (editor) {
                                          editor.editing.view.document.on(
                                            'enter',
                                            (evt, data) => {
                                              if (data.isSoft) {
                                                editor.execute('enter');
                                              } else {
                                                editor.execute('shiftEnter');
                                              }

                                              data.preventDefault();
                                              evt.stop();
                                              editor.editing.view.scrollToTheSelection();
                                            },
                                            {
                                              priority: 'high'
                                            }
                                          );
                                        }
                                      }}
                                      onChange={(event, editor) => {
                                        const data = editor.getData();
                                        const cleaned = DOMPurify.sanitize(
                                          data,
                                          {
                                            ALLOWED_TAGS: [
                                              'br',
                                              'b',
                                              'a',
                                              'u',
                                              'i',
                                              'strong',
                                              'img',
                                              'button'
                                            ],
                                            ADD_ATTR: ['target']
                                          }
                                        );
                                        setFieldValue(field.name, cleaned);
                                      }}
                                    />
                                  )}
                                />
                              </div>
                              <div className="add-qa-answer-remove">
                                {index > 0 && (
                                  <button
                                    type="button"
                                    className="add-qa-answer-remove-button"
                                    onClick={() => arrayHelpers.remove(index)} // remove a friend from the list
                                  >
                                    x
                                  </button>
                                )}
                              </div>
                              {index === values.answers.length - 1 && (
                                <div className="add-qa-answer-add">
                                  <button
                                    type="button"
                                    className="add-qa-answer-add-button"
                                    onClick={() =>
                                      arrayHelpers.push({
                                        text: '',
                                        lang: 'finnish'
                                      })
                                    } // insert an empty string at a position
                                  >
                                    + Lisää
                                  </button>
                                </div>
                              )}
                            </div>
                          ))}
                      </div>
                    )}
                  />
                  <div className="modal-content-form-header">Tagit</div>
                  <Field
                    name="tags"
                    render={({
                      field,
                      form: { setFieldValue, setFieldTouched }
                    }) => (
                      <div className="add-qa-tags">
                        <TagSelect
                          options={
                            data.tags
                              ? data.tags.map(tag => ({
                                  value: tag.id,
                                  label: tag.name
                                }))
                              : []
                          }
                          value={values.tags}
                          onChange={setFieldValue}
                          onBlur={setFieldTouched}
                        />
                      </div>
                    )}
                  />
                </Form>
              </div>
            </div>
            <div className="modal-footer">
              {isSubmitting && 'Tallennetaan...'}
              {!isSubmitting && (
                <button
                  className="modal-footer-submit-button"
                  type="submit"
                  onClick={handleSubmit}
                >
                  Submit
                </button>
              )}
            </div>
          </Fragment>
        )}
      />
    </Fragment>
  );
};

export default AddQaModal;
